/**
 * gm-product.js
 * Gilbert Maxwell PDP engine: scroll-snap gallery, variant matcher,
 * and add to bag flow. One module, custom elements guarded per theme law.
 *
 * Money law: this module never builds a price string. Formatted prices come
 * from the Liquid data island (price_formatted / compare_formatted per variant).
 */

import { CartAddEvent } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

const ATC_TIMEOUT_MS = 12000;
const ATC_WATCHDOG_MS = 15000;
const ATC_RESET_MS = 1600;

/**
 * Native scroll-snap gallery. The browser owns the swipe (momentum, rubber
 * banding, one slide per flick); this element only mirrors the position into
 * the counter and thumbnails, answers programmatic jumps, and gives mouse
 * users a drag on desktop.
 */
class GmProductGallery extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {HTMLElement | null} */
  #viewport = null;
  /** @type {HTMLElement | null} */
  #track = null;
  /** @type {HTMLElement | null} */
  #counterCurrent = null;
  /** @type {HTMLElement[]} */
  #thumbs = [];
  /** @type {HTMLElement[]} */
  #slides = [];
  #index = 0;
  #raf = 0;
  #dragging = false;
  #dragStartX = 0;
  #dragStartScroll = 0;
  #dragMoved = false;
  /** @type {number | null} */
  #pointerId = null;

  connectedCallback() {
    if (this.#controller) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#viewport = this.querySelector('[data-gm-viewport]');
    this.#track = this.querySelector('[data-gm-track]');
    this.#counterCurrent = this.querySelector('[data-gm-counter-current]');
    this.#thumbs = Array.from(this.querySelectorAll('[data-gm-thumb]'));
    if (!this.#viewport || !this.#track) return;
    this.#slides = Array.from(this.#track.querySelectorAll('[data-gm-slide]'));
    if (this.#slides.length === 0) return;

    this.#track.addEventListener('scroll', this.#onScroll, { signal, passive: true });
    this.#track.addEventListener('scrollend', this.#onScrollEnd, { signal });
    this.#viewport.addEventListener('keydown', this.#onKeydown, { signal });
    this.#viewport.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.#viewport.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.#viewport.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.#viewport.addEventListener('pointercancel', this.#onPointerUp, { signal });
    this.#viewport.addEventListener('click', this.#onClickCapture, { signal, capture: true });
    window.addEventListener('resize', this.#onResize, { signal });

    this.#thumbs.forEach((thumb) => {
      thumb.addEventListener(
        'click',
        () => {
          const realIndex = Number(thumb.dataset.index);
          if (!Number.isNaN(realIndex)) this.goToReal(realIndex);
        },
        { signal }
      );
    });

    // Browsers may restore a stale scroll offset on back navigation.
    this.#track.scrollLeft = 0;
    this.#updateUI();
  }

  disconnectedCallback() {
    if (this.#controller) this.#controller.abort();
    this.#controller = null;
    cancelAnimationFrame(this.#raf);
  }

  get #slideWidth() {
    return this.#track ? this.#track.clientWidth : 0;
  }

  /**
   * Jumps to a real (0 based) slide index.
   * @param {number} realIndex
   * @param {boolean} [animate]
   */
  goToReal(realIndex, animate = true) {
    if (!this.#track || realIndex < 0 || realIndex >= this.#slides.length) return;
    const width = this.#slideWidth;
    if (!width) return;
    this.#index = realIndex;
    this.#updateUI();
    const smooth = animate && !prefersReducedMotion();
    this.#track.scrollTo({ left: realIndex * width, behavior: smooth ? 'smooth' : 'auto' });
  }

  /**
   * Syncs the gallery to a variant featured image, matched by URL basename.
   * @param {string} src
   * @param {boolean} [animate]
   */
  goToBySrc(src, animate = true) {
    if (!src || this.#slides.length === 0) return;
    const basename = String(src).split('?')[0].split('/').pop();
    if (!basename) return;
    for (let i = 0; i < this.#slides.length; i += 1) {
      const slideSrc = (this.#slides[i].getAttribute('data-media-src') || '').split('?')[0];
      if (slideSrc.split('/').pop() === basename) {
        this.goToReal(i, animate);
        return;
      }
    }
  }

  #indexFromScroll() {
    const width = this.#slideWidth;
    if (!this.#track || !width) return this.#index;
    const raw = Math.round(this.#track.scrollLeft / width);
    return Math.min(this.#slides.length - 1, Math.max(0, raw));
  }

  #onScroll = () => {
    cancelAnimationFrame(this.#raf);
    this.#raf = requestAnimationFrame(() => {
      const next = this.#indexFromScroll();
      if (next !== this.#index) {
        this.#index = next;
        this.#updateUI();
      }
    });
  };

  #onScrollEnd = () => {
    this.#index = this.#indexFromScroll();
    this.#updateUI();
  };

  #updateUI() {
    const real = this.#index;
    if (this.#counterCurrent) {
      this.#counterCurrent.textContent = String(real + 1).padStart(2, '0');
    }
    this.#thumbs.forEach((thumb) => {
      const isActive = Number(thumb.dataset.index) === real;
      thumb.classList.toggle('is-active', isActive);
      if (isActive) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
  }

  /** Mouse drag on desktop; touch is left to the browser. @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (this.#slides.length < 2 || !this.#viewport || !this.#track) return;
    this.#dragging = true;
    this.#dragMoved = false;
    this.#pointerId = event.pointerId;
    this.#dragStartX = event.clientX;
    this.#dragStartScroll = this.#track.scrollLeft;
    this.#track.style.scrollSnapType = 'none';
    this.#track.style.scrollBehavior = 'auto';
    this.#viewport.classList.add('is-dragging');
    try {
      this.#viewport.setPointerCapture(event.pointerId);
    } catch (error) {
      // Pointer capture is best effort.
    }
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#dragging || event.pointerId !== this.#pointerId || !this.#track) return;
    const deltaX = event.clientX - this.#dragStartX;
    if (Math.abs(deltaX) > 3) this.#dragMoved = true;
    this.#track.scrollLeft = this.#dragStartScroll - deltaX;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#dragging || event.pointerId !== this.#pointerId || !this.#viewport || !this.#track) return;
    this.#dragging = false;
    this.#viewport.classList.remove('is-dragging');
    try {
      this.#viewport.releasePointerCapture(event.pointerId);
    } catch (error) {
      // Already released.
    }
    this.#pointerId = null;
    const deltaX = event.clientX - this.#dragStartX;
    const width = this.#slideWidth;
    const start = width ? Math.round(this.#dragStartScroll / width) : this.#index;
    const threshold = Math.max(40, width * 0.15);
    let next = start;
    if (deltaX < -threshold) next += 1;
    else if (deltaX > threshold) next -= 1;
    next = Math.min(this.#slides.length - 1, Math.max(0, next));
    this.#track.style.scrollSnapType = '';
    this.#track.style.scrollBehavior = '';
    this.goToReal(next, true);
  };

  /** Swallows the click that ends a mouse drag. @param {MouseEvent} event */
  #onClickCapture = (event) => {
    if (!this.#dragMoved) return;
    this.#dragMoved = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.target !== this.#viewport || this.#slides.length < 2) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goToReal(this.#index > 0 ? this.#index - 1 : this.#slides.length - 1, true);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goToReal(this.#index < this.#slides.length - 1 ? this.#index + 1 : 0, true);
    }
  };

  #onResize = () => {
    // Keep the current slide in place when the slide width changes.
    this.goToReal(this.#index, false);
  };
}
/**
 * Variant engine and add to bag. Matches variants with options.every against
 * the Liquid data island, keeps the URL in sync, and never lets the add
 * button stick in a pending state.
 */
class GmProductBuy extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {Array<any>} */
  #variants = [];
  /** @type {Record<string, {price_formatted: string, compare_formatted: string | null}>} */
  #prices = {};
  /** @type {string[]} */
  #selected = [];
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLInputElement | null} */
  #variantInput = null;
  /** @type {HTMLInputElement | null} */
  #qtyInput = null;
  /** @type {HTMLElement | null} */
  #priceEl = null;
  /** @type {HTMLElement | null} */
  #compareEl = null;
  /** @type {HTMLElement | null} */
  #pillEl = null;
  /** @type {HTMLElement | null} */
  #stockEl = null;
  /** @type {HTMLButtonElement | null} */
  #atc = null;
  /** @type {HTMLElement | null} */
  #atcText = null;
  /** @type {HTMLElement | null} */
  #errorEl = null;
  /** @type {HTMLElement | null} */
  #liveEl = null;
  #busy = false;
  #watchdog = 0;
  #resetTimer = 0;
  #errorTimer = 0;
  #liveTimer = 0;

  connectedCallback() {
    if (this.#controller) return;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    const island = document.getElementById(`gm-product-data-${this.dataset.sectionId || ''}`);
    if (!island) return;
    try {
      const parsed = JSON.parse(island.textContent || '{}');
      this.#variants = Array.isArray(parsed.variants) ? parsed.variants : [];
      this.#prices = parsed.prices && typeof parsed.prices === 'object' ? parsed.prices : {};
    } catch (error) {
      return;
    }
    if (this.#variants.length === 0) return;

    this.#form = this.querySelector('[data-gm-form]');
    this.#variantInput = this.querySelector('[data-gm-variant-id]');
    this.#qtyInput = this.querySelector('[data-gm-qty]');
    this.#priceEl = this.querySelector('[data-gm-price]');
    this.#compareEl = this.querySelector('[data-gm-compare]');
    this.#pillEl = this.querySelector('[data-gm-pill]');
    this.#stockEl = this.querySelector('[data-gm-stock]');
    this.#atc = this.querySelector('[data-gm-atc]');
    this.#atcText = this.querySelector('[data-gm-atc-text]');
    this.#errorEl = this.querySelector('[data-gm-error]');
    this.#liveEl = this.querySelector('[data-gm-live]');

    // Seed selections from the server-rendered variant, then overlay whatever
    // the rendered option buttons mark as selected.
    const initialId = Number(this.#variantInput?.value || 0);
    const initialVariant = this.#variants.find((variant) => variant.id === initialId) || this.#variants[0];
    if (initialVariant && Array.isArray(initialVariant.options)) {
      this.#selected = initialVariant.options.slice();
    }
    this.querySelectorAll('[data-gm-option-group]').forEach((group) => {
      const chosen = group.querySelector('[data-gm-option-value].is-selected');
      if (chosen instanceof HTMLElement) {
        const index = Number(chosen.dataset.optionIndex);
        if (!Number.isNaN(index)) this.#selected[index] = chosen.dataset.value || '';
      }
    });

    this.querySelectorAll('[data-gm-option-value]').forEach((button) => {
      button.addEventListener('click', () => this.#onOptionClick(/** @type {HTMLElement} */ (button)), { signal });
    });

    this.querySelector('[data-gm-qty-minus]')?.addEventListener('click', () => this.#stepQuantity(-1), { signal });
    this.querySelector('[data-gm-qty-plus]')?.addEventListener('click', () => this.#stepQuantity(1), { signal });
    this.#qtyInput?.addEventListener('change', () => this.#clampQuantity(), { signal });

    this.#form?.addEventListener('submit', this.#onSubmit, { signal });

    this.#render(false);
  }

  disconnectedCallback() {
    if (this.#controller) this.#controller.abort();
    this.#controller = null;
    clearTimeout(this.#watchdog);
    clearTimeout(this.#resetTimer);
    clearTimeout(this.#errorTimer);
    clearTimeout(this.#liveTimer);
  }

  #currentVariant() {
    return this.#variants.find(
      (variant) =>
        Array.isArray(variant.options) &&
        variant.options.every((option, index) => option === this.#selected[index])
    );
  }

  /**
   * True when at least one available variant carries this value at this option
   * position while matching every other currently selected option.
   * @param {number} optionIndex
   * @param {string} value
   */
  #valueAvailable(optionIndex, value) {
    return this.#variants.some(
      (variant) =>
        variant.available &&
        Array.isArray(variant.options) &&
        variant.options.every((option, index) => (index === optionIndex ? option === value : option === this.#selected[index]))
    );
  }

  /** @param {HTMLElement} button */
  #onOptionClick(button) {
    const index = Number(button.dataset.optionIndex);
    if (Number.isNaN(index)) return;
    const group = button.closest('[data-gm-option-group]');
    group?.querySelectorAll('[data-gm-option-value]').forEach((other) => {
      const isChosen = other === button;
      other.classList.toggle('is-selected', isChosen);
      other.setAttribute('aria-pressed', String(isChosen));
    });
    this.#selected[index] = button.dataset.value || '';
    this.#render(true);
  }

  /** @param {boolean} userAction - true when triggered by a click, false on initial sync */
  #render(userAction) {
    const variant = this.#currentVariant();

    if (variant) {
      const entry = this.#prices[String(variant.id)];
      if (entry && this.#priceEl) this.#priceEl.textContent = entry.price_formatted;
      const onSale = Boolean(
        entry &&
          entry.compare_formatted &&
          typeof variant.compare_at_price === 'number' &&
          variant.compare_at_price > variant.price
      );
      if (this.#compareEl) {
        this.#compareEl.hidden = !onSale;
        if (onSale && entry && entry.compare_formatted) this.#compareEl.textContent = entry.compare_formatted;
      }
      if (this.#pillEl) this.#pillEl.hidden = !onSale;
      if (this.#variantInput) this.#variantInput.value = String(variant.id);
    } else {
      if (this.#compareEl) this.#compareEl.hidden = true;
      if (this.#pillEl) this.#pillEl.hidden = true;
    }

    if (this.#stockEl) this.#stockEl.hidden = !(variant && variant.available);

    if (!this.#busy && this.#atc) {
      if (!variant) this.#setAtc('unavailable', true);
      else if (!variant.available) this.#setAtc('soldout', true);
      else this.#setAtc('add', false);
    }

    this.querySelectorAll('[data-gm-selected-label]').forEach((label) => {
      const index = Number(label.getAttribute('data-option-index'));
      if (!Number.isNaN(index) && this.#selected[index] != null) label.textContent = this.#selected[index];
    });

    this.querySelectorAll('[data-gm-option-group]:not([data-option-kind="color"]) [data-gm-option-value]').forEach(
      (button) => {
        if (!(button instanceof HTMLElement)) return;
        const index = Number(button.dataset.optionIndex);
        if (Number.isNaN(index)) return;
        const available = this.#valueAvailable(index, button.dataset.value || '');
        button.classList.toggle('is-unavailable', !available);
        const note = button.querySelector('[data-gm-avail-note]');
        if (note) note.textContent = available ? '' : 'Unavailable';
      }
    );

    if (userAction && variant) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url.toString());
      } catch (error) {
        // URL sync is cosmetic; fail quiet.
      }
    }

    if (variant && variant.featured_image && variant.featured_image.src) {
      const gallery = this.closest('.gm-product')?.querySelector('gm-product-gallery');
      if (gallery && typeof (/** @type {any} */ (gallery).goToBySrc) === 'function') {
        /** @type {any} */ (gallery).goToBySrc(variant.featured_image.src, userAction);
      }
    }
  }

  /** @param {number} delta */
  #stepQuantity(delta) {
    if (!this.#qtyInput) return;
    const current = parseInt(this.#qtyInput.value, 10);
    const next = (Number.isNaN(current) ? 1 : current) + delta;
    this.#qtyInput.value = String(Math.max(1, next));
  }

  #clampQuantity() {
    if (!this.#qtyInput) return;
    const current = parseInt(this.#qtyInput.value, 10);
    this.#qtyInput.value = String(Number.isNaN(current) || current < 1 ? 1 : current);
  }

  /**
   * @param {'add' | 'adding' | 'added' | 'soldout' | 'unavailable'} state
   * @param {boolean} disabled
   */
  #setAtc(state, disabled) {
    if (!this.#atc) return;
    const labels = {
      add: this.#atc.dataset.labelAdd,
      adding: this.#atc.dataset.labelAdding,
      added: this.#atc.dataset.labelAdded,
      soldout: this.#atc.dataset.labelSoldout,
      unavailable: this.#atc.dataset.labelUnavailable,
    };
    this.#atc.disabled = disabled;
    const label = labels[state];
    if (this.#atcText && label) this.#atcText.textContent = label;
  }

  /** @param {string} message */
  #showError(message) {
    if (!this.#errorEl) return;
    this.#errorEl.textContent = message;
    this.#errorEl.hidden = false;
    clearTimeout(this.#errorTimer);
    this.#errorTimer = setTimeout(() => {
      if (this.#errorEl) this.#errorEl.hidden = true;
    }, 6000);
  }

  #hideError() {
    if (this.#errorEl) this.#errorEl.hidden = true;
    clearTimeout(this.#errorTimer);
  }

  /** @param {string} message */
  #announce(message) {
    if (!this.#liveEl) return;
    this.#liveEl.textContent = message;
    clearTimeout(this.#liveTimer);
    this.#liveTimer = setTimeout(() => {
      if (this.#liveEl) this.#liveEl.textContent = '';
    }, 4000);
  }

  /** Recovers the button to an honest state after any failure or stall. */
  #recover() {
    this.#busy = false;
    clearTimeout(this.#watchdog);
    this.#render(false);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#busy || !this.#form || !this.#atc || this.#atc.disabled) return;

    const formData = new FormData(this.#form);
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    if (sectionIds.length > 0) formData.append('sections', sectionIds.join(','));

    this.#busy = true;
    this.#setAtc('adding', true);

    // Watchdog: whatever happens below, the button never sticks on "Adding".
    clearTimeout(this.#watchdog);
    this.#watchdog = setTimeout(() => this.#recover(), ATC_WATCHDOG_MS);

    const abortController = new AbortController();
    const abortTimer = setTimeout(() => abortController.abort(), ATC_TIMEOUT_MS);
    let redirecting = false;

    try {
      const routes = typeof Theme !== 'undefined' && Theme && Theme.routes ? Theme.routes : null;
      const addUrl = (routes && routes.cart_add_url) || '/cart/add.js';

      const response = await fetch(addUrl, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
        signal: abortController.signal,
      });

      let data = null;
      try {
        data = await response.json();
      } catch (parseError) {
        data = null;
      }

      if (!response.ok || !data || data.status) {
        this.#showError((data && (data.description || data.message)) || 'Could not add to bag. Please try again.');
        this.#recover();
        return;
      }

      this.#hideError();

      const variantId = String(formData.get('id') || '');
      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'product-form-component',
          itemCount: Number(formData.get('quantity')) || 1,
          productId: this.dataset.productId,
          variantId,
          sections: data.sections,
        })
      );

      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && typeof (/** @type {any} */ (drawer).open) === 'function') {
        /** @type {any} */ (drawer).open();
      } else {
        // No drawer on this store (cart type page): the cart page is the feedback.
        redirecting = true;
        window.location.href = (routes && routes.cart_url) || '/cart';
        return;
      }

      this.#setAtc('added', true);
      this.#announce(this.#atc.dataset.labelAdded || '');
      clearTimeout(this.#resetTimer);
      this.#resetTimer = setTimeout(() => this.#recover(), ATC_RESET_MS);
    } catch (error) {
      if (!redirecting) {
        this.#showError('Could not add to bag. Please try again.');
        this.#recover();
      }
    } finally {
      clearTimeout(abortTimer);
      if (!redirecting) clearTimeout(this.#watchdog);
    }
  };
}

if (!customElements.get('gm-product-gallery')) {
  customElements.define('gm-product-gallery', GmProductGallery);
}

if (!customElements.get('gm-product-buy')) {
  customElements.define('gm-product-buy', GmProductBuy);
}
